import React, { useState, useEffect, useRef } from "react";
import { useAuthStore } from "../../../store/authStore";
import { useEventStore } from "../../../store/eventStore";
import AssignModal from "./AssignModal";
import PrepairAssignModal from "./PrepairAssignModal";
import SelectTrainNEngineModal from "./SelectTrainNEngineModal";
import {
	CalendarIcon,
	XMarkIcon,
	MagnifyingGlassIcon,
	ChevronUpIcon,
	ChevronDownIcon,
	UserGroupIcon,
	SparklesIcon,
	EnvelopeIcon,
} from "@heroicons/react/24/outline";
import { BiTrip } from "react-icons/bi";

export default function EventSeat() {
	const { user } = useAuthStore();
	const { getEventsByDate, getEngines, getTrainList, isLoading, error } =
		useEventStore();

	// 상태 관리
	const [seatData, setSeatData] = useState([]);
	const [searchDate, setSearchDate] = useState("");
	const [searchType, setSearchType] = useState("go"); // 'go'(왕편) 또는 'back'(복편)
	const [localLoading, setLocalLoading] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedSeat, setSelectedSeat] = useState(null);
	const [isPrepareModalOpen, setIsPrepareModalOpen] = useState(false);
	const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
	const [selectedEvent, setSelectedEvent] = useState(null);
	const [selectedIndexes, setSelectedIndexes] = useState([]); // 선택된 인덱스 배열
	const [selectAll, setSelectAll] = useState(false); // 전체 선택 상태
	const initialLoadRef = useRef(true);

	// 모달 상태 관리
	const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
	const [isAssignModalOpenState, setIsAssignModalOpenState] = useState(false);
	const [isPrepairAssignModalOpenState, setIsPrepairAssignModalOpenState] =
		useState(false);
	const [isSelectTrainModalOpen, setIsSelectTrainModalOpen] = useState(false);
	const [selectedEventData, setSelectedEventData] = useState(null);

	// 모달 간 데이터 전달을 위한 상태
	const [selectedTrain, setSelectedTrain] = useState(null);
	const [selectedEngine, setSelectedEngine] = useState(null);
	const [selectedSeatmap, setSelectedSeatmap] = useState(null);
	const [preparedAssignData, setPreparedAssignData] = useState(null);

	// 전체 선택/해제 처리 함수
	const handleSelectAll = () => {
		const newSelectAll = !selectAll;
		setSelectAll(newSelectAll);

		if (newSelectAll) {
			// 모든 항목 선택
			const allIndexes = seatData.map((_, index) => index);
			setSelectedIndexes(allIndexes);
		} else {
			// 모든 선택 해제
			setSelectedIndexes([]);
		}
	};

	// 개별 체크박스 선택/해제 처리 함수
	const handleSelectItem = (index) => {
		const selectedIndex = selectedIndexes.indexOf(index);
		let newSelectedIndexes = [...selectedIndexes];

		if (selectedIndex === -1) {
			// 선택되지 않은 항목이면 추가
			newSelectedIndexes.push(index);
		} else {
			// 이미 선택된 항목이면 제거
			newSelectedIndexes.splice(selectedIndex, 1);
		}

		setSelectedIndexes(newSelectedIndexes);

		// 모든 항목이 선택되었는지 확인
		setSelectAll(newSelectedIndexes.length === seatData.length);
	};

	// 좌석배치실행 버튼 클릭 핸들러
	const handleSeatAssignClick = () => {
		if (selectedIndexes.length === 0) {
			alert("좌석을 배치할 항목을 선택해주세요.");
			return;
		}

		// 선택된 이벤트 데이터 추출
		const selectedEvents = selectedIndexes.map((index) => seatData[index]);
		setSelectedEventData(selectedEvents);
		setIsSelectTrainModalOpen(true);
	};

	// 페이지 로드 시 오늘 날짜로 설정
	useEffect(() => {
		let today = new Date().toISOString().split("T")[0];
		// today = "2023-10-09";
		setSearchDate(today);
	}, []);

	// 초기 로딩 시 검색 실행
	useEffect(() => {
		if (initialLoadRef.current && searchDate) {
			handleSearch();
			initialLoadRef.current = false;
		}
	}, [searchDate]);

	// 왕편/복편 전환 시 자동 재검색
	useEffect(() => {
		if (!searchDate || initialLoadRef.current) {
			return;
		}
		handleSearch();
	}, [searchType]);

	// 날짜 필터 초기화
	const handleClearDate = () => {
		setSearchDate("");
	};

	// 좌석 데이터 가져오기
	const handleSearch = async () => {
		if (!searchDate) {
			return;
		}

		setLocalLoading(true);
		try {
			// YYYY-MM-DD → YYYYMMDD 형식 변환
			const formattedDate = searchDate.replace(/-/g, "");
			const result = await getEventsByDate(formattedDate, formattedDate, searchType);
			if (result.success) {
				// V2 API 응답을 기존 형식에 맞게 변환
				const transformedData = (result.data || []).map(item => ({
					ev_cd: `${item.ev_ym}-${item.ev_seq}`,
					ev_nm: item.ev_nm,
					start_day: item.start_day,
					end_day: item.start_day, // V2에서는 end_day 없음
					reservation_count: item.reservation_count,
				}));
				setSeatData(transformedData);
			} else {
				console.error("행사 데이터 가져오기 실패:", result.error);
				setSeatData([]);
			}
		} catch (error) {
			console.error("행사 데이터 가져오기 중 오류:", error);
			setSeatData([]);
		} finally {
			setLocalLoading(false);
		}
	};

	const SearchBox = (
		<div className="bg-white shadow rounded-lg p-6 mb-6">
			{/* ① 한 줄 레이아웃 */}
			<div className="flex flex-nowrap items-center justify-between gap-4">
				{/* ───── 왼쪽 묶음: 토글·날짜·검색 ───── */}
				<div className="flex flex-nowrap items-center gap-4">
					{/* 왕편/복편 토글 */}
					<div className="relative flex items-center">
						<label
							htmlFor="tripType"
							className="mr-2 text-sm font-medium text-gray-700"
						>
							열차 유형
						</label>
						<div className="inline-flex items-center p-1 bg-gray-100 rounded-lg shadow-sm">
							<button
								type="button"
								onClick={() => setSearchType("go")}
								className={`relative flex items-center justify-center px-4 py-2 mr-2 text-sm font-medium transition-all duration-200 ${
									searchType === "go"
										? "bg-blue-600 text-white shadow-md scale-105"
										: "bg-transparent text-gray-700 hover:bg-gray-200"
								} rounded-lg`}
							>
								왕편(출발)
							</button>
							<button
								type="button"
								onClick={() => setSearchType("back")}
								className={`relative flex items-center justify-center px-4 py-2 text-sm font-medium transition-all duration-200 ${
									searchType === "back"
										? "bg-blue-600 text-white shadow-md scale-105"
										: "bg-transparent text-gray-700 hover:bg-gray-200"
								} rounded-lg`}
							>
								복편(도착)
							</button>
						</div>
					</div>

					{/* 날짜 선택 */}
					<div className="relative flex items-center">
						<label
							htmlFor="searchDate"
							className="mr-2 text-sm font-medium text-gray-700"
						>
							출발일자
						</label>
						<div className="relative">
							<input
								type="date"
								id="searchDate"
								value={searchDate}
								onChange={(e) => setSearchDate(e.target.value)}
								className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
							/>
							<CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
							{searchDate && (
								<button
									onClick={handleClearDate}
									className="absolute right-2 top-1/2 -translate-y-1/2"
								>
									<XMarkIcon className="h-4 w-4 text-gray-400" />
								</button>
							)}
						</div>
					</div>

					{/* 검색 버튼 */}
					<button
						onClick={handleSearch}
						disabled={localLoading}
						className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm shadow-sm hover:shadow-md transition disabled:opacity-50"
					>
						<div className="flex items-center">
							{localLoading ? (
								<>
									<svg
										className="animate-spin h-4 w-4 mr-1"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										/>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										/>
									</svg>
									검색 중...
								</>
							) : (
								<>
									<MagnifyingGlassIcon className="h-4 w-4 mr-1" />
									검색하기
								</>
							)}
						</div>
					</button>
				</div>

				{/* ───── 오른쪽: 좌석배치실행 (자동 spacer 효과) ───── */}
				<button
					onClick={handleSeatAssignClick}
					className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
				>
					<SparklesIcon className="h-6 w-5 mr-2" />
					좌석배치실행
				</button>
			</div>
		</div>
	);

	// 날짜 포맷 함수 (YYYYMMDD → YYYY-MM-DD)
	const formatDate = (dateStr) => {
		if (!dateStr || dateStr.length !== 8) return dateStr || "-";
		return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
	};

	const DataBox = (
		<div className="overflow-x-auto">
			<table className="min-w-full divide-y divide-gray-200">
				<thead className="bg-gray-50">
					<tr>
						<th
							scope="col"
							className="pl-6 pr-3 pt-2 pb-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
						>
							<input
								type="checkbox"
								checked={selectAll}
								onChange={handleSelectAll}
								className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
							/>
						</th>
						<th
							scope="col"
							className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
						>
							번호
						</th>
						<th
							scope="col"
							className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
						>
							행사명
						</th>
						<th
							scope="col"
							className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
						>
							출발일
						</th>
						<th
							scope="col"
							className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
						>
							예약건수
						</th>
					</tr>
				</thead>
				<tbody className="bg-white divide-y divide-gray-200">
					{seatData && seatData.length > 0 ? (
						seatData.map((item, index) => (
							<tr key={index} className={selectedIndexes.includes(index) ? "bg-blue-50" : ""}>
								<td className="pl-6 pr-3 py-3 whitespace-nowrap">
									<input
										type="checkbox"
										checked={selectedIndexes.includes(
											index
										)}
										onChange={() => handleSelectItem(index)}
										className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
									/>
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
									{index + 1}
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
									{item.ev_nm || "-"}
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
									{formatDate(item.start_day)}
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
									<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
										{item.reservation_count || 0}건
									</span>
								</td>
							</tr>
						))
					) : (
						<tr>
							<td
								colSpan="5"
								className="px-3 py-3 text-center text-sm text-gray-500"
							>
								{error ? (
									<div className="text-red-500">
										<p>오류가 발생했습니다.</p>
										<p>{error}</p>
									</div>
								) : (
									<div className="flex flex-col items-center">
										<BiTrip className="h-10 w-10 text-gray-400 mb-2" />
										<p>조회된 행사 정보가 없습니다.</p>
										<p className="text-sm">
											날짜를 선택하고 검색해 주세요.
										</p>
									</div>
								)}
							</td>
						</tr>
					)}
				</tbody>
			</table>
		</div>
	);

	return (
		<div className="py-6">
			<h1 className="text-2xl font-bold text-gray-900 mb-6">좌석 배정</h1>
			<p className="text-gray-600 mb-4">좌석을 배정하는 페이지입니다.</p>

			{/* 검색 필터 영역 */}
			{SearchBox}

			{/* 좌석 데이터 테이블 */}
			<div className="bg-white shadow rounded-lg overflow-hidden">
				{localLoading ? (
					<div className="flex justify-center items-center p-12">
						<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
					</div>
				) : (
					DataBox
				)}
			</div>

			{/* 모달 컴포넌트들 */}
			<SelectTrainNEngineModal
				isOpen={isSelectTrainModalOpen}
				onClose={() => setIsSelectTrainModalOpen(false)}
				eventDataList={selectedEventData}
				searchDate={searchDate}
				onConfirm={(trainData, engineData, seatmapLayout) => {
					// 첫 번째 모달에서 선택한 데이터 저장
					setSelectedTrain(trainData);
					setSelectedEngine(engineData);
					setSelectedSeatmap(seatmapLayout || null);
					// 첫 번째 모달 닫고 두 번째 모달 열기
					setIsSelectTrainModalOpen(false);
					setIsPrepairAssignModalOpenState(true);
				}}
			/>
			<PrepairAssignModal
				isOpen={isPrepairAssignModalOpenState}
				onClose={() => setIsPrepairAssignModalOpenState(false)}
				searchType={searchType}
				eventData={selectedEventData}
				trainData={selectedTrain}
				engineData={selectedEngine}
				onPrepare={(eventData, assignData) => {
					// 두 번째 모달에서 준비한 데이터 저장
					setPreparedAssignData({
						eventData,
						assignData,
						trainData: selectedTrain,
						engineData: selectedEngine,
						seatmapLayout: selectedSeatmap,
					});
					// 두 번째 모달 닫고 세 번째 모달 열기
					setIsPrepairAssignModalOpenState(false);
					setIsAssignModalOpenState(true);
				}}
			/>
			<AssignModal
				isOpen={isAssignModalOpenState}
				onClose={() => {
					setIsAssignModalOpenState(false);
					// 모든 모달 데이터 초기화
					setSelectedTrain(null);
					setSelectedEngine(null);
					setSelectedSeatmap(null);
					setPreparedAssignData(null);
				}}
				searchType={searchType}
				toAssignData={preparedAssignData}
			/>
		</div>
	);
}
